import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Cookie, Settings, CheckCircle, XCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from "@/components/ui/use-toast";
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const CookiePolicyPage = () => {
  const location = useLocation();
  const { toast } = useToast();
  const pageTitle = "Cookie Policy | WebFiny";
  const pageDescription = "Learn about how WebFiny uses cookies and similar tracking technologies on our website and services to enhance your experience and analyze usage.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;
  const lastUpdated = "June 15, 2025";

  const handleManagePreferences = () => {
    toast({
      title: "🚧 Feature In Progress",
      description: "Cookie preference management from this page is coming soon. For now, please use the banner or your browser settings.",
      className: "bg-blue-600 text-white"
    });
  };

  const sections = [
    {
      id: "what-are-cookies",
      icon: <Cookie className="w-6 h-6 mr-3 text-primary" />,
      title: "1. What Are Cookies?",
      content: `Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work, or work more efficiently, as well as to provide information to the owners of the site. Cookies help us remember your preferences, understand how you interact with our Services, and personalize your experience.`
    },
    {
      id: "how-we-use-cookies",
      icon: <Settings className="w-6 h-6 mr-3 text-primary" />,
      title: "2. How We Use Cookies",
      content: `We use cookies for several purposes:
        <ul class="list-disc list-inside space-y-1 mt-2">
          <li><strong>Necessary Cookies:</strong> These cookies are essential for you to browse the website and use its features, such as accessing secure areas of the site. Without these cookies, services like user authentication and account management cannot be provided.</li>
          <li><strong>Preferences Cookies:</strong> These cookies allow our website to remember choices you make (such as your user name, language, or the region you are in) and provide enhanced, more personal features. For example, they can remember your login details.</li>
          <li><strong>Analytics/Performance Cookies:</strong> These cookies collect information about how you use our website, for instance, which pages you go to most often, and if you get error messages from web pages. These cookies don’t collect information that identifies you. All information these cookies collect is aggregated and therefore anonymous. It is only used to improve how our website works.</li>
          <li><strong>Marketing Cookies:</strong> These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third-party advertisers. We may use these to deliver targeted advertising based on your interests.</li>
        </ul>`
    },
    {
      id: "types-of-cookies",
      icon: <Info className="w-6 h-6 mr-3 text-primary" />,
      title: "3. Types of Cookies We Use",
      content: `
        <ul class="list-disc list-inside space-y-1 mt-2">
          <li><strong>Session Cookies:</strong> These are temporary cookies that remain in the cookie file of your browser until you leave the site.</li>
          <li><strong>Persistent Cookies:</strong> These remain in the cookie file of your browser for much longer (though how long will depend on the lifetime of the specific cookie). Persistent cookies are used to remember your preferences over multiple sessions.</li>
          <li><strong>First-party Cookies:</strong> These are set by the website you are visiting (WebFiny).</li>
          <li><strong>Third-party Cookies:</strong> These are set by a third party separate from the website you are visiting, such as our analytics or advertising partners. We do not control the use of these third-party cookies and you should check the relevant third party's website for more information.</li>
        </ul>`
    },
    {
      id: "managing-cookies",
      icon: <CheckCircle className="w-6 h-6 mr-3 text-primary" />,
      title: "4. Your Choices & Managing Cookies",
      content: `You have several options for managing cookies:
        <ul class="list-disc list-inside space-y-1 mt-2">
          <li><strong>Cookie Consent Banner/Tool:</strong> When you first visit our site, you will be presented with a cookie consent banner that allows you to accept all cookies, reject non-essential cookies, or customize your preferences. You can change your preferences at any time via our cookie preferences tool (often found in the footer or settings).</li>
          <li><strong>Browser Settings:</strong> Most web browsers allow some control of most cookies through the browser settings. You can set your browser to block or alert you about these cookies, but some parts of the site may not then work. To find out more about cookies, including how to see what cookies have been set and how to manage and delete them, visit <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">www.allaboutcookies.org</a>.</li>
          <li><strong>Opt-out Links:</strong> For some third-party cookies (e.g., analytics, advertising), you may be able to opt-out directly via links provided by those third parties (e.g., Google Analytics Opt-out Browser Add-on).</li>
        </ul>
        Please note that if you choose to block or delete cookies, some features of our Services may not operate correctly.`
    },
    {
      id: "policy-changes",
      icon: <Info className="w-6 h-6 mr-3 text-primary" />,
      title: "5. Changes to This Cookie Policy",
      content: `We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any significant changes by posting the new policy on our website and updating the "Last Updated" date.`
    },
    {
      id: "contact-us",
      icon: <XCircle className="w-6 h-6 mr-3 text-primary" />, 
      title: "6. Contact Us",
      content: `If you have any questions about our use of cookies or this Cookie Policy, please contact us at: <a href="mailto:privacy@webfiny.com" class="text-primary hover:underline">privacy@webfiny.com</a>.`
    }
  ];

  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": canonicalUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
                { "@type": "ListItem", "position": 2, "name": "Cookie Policy" }
              ]
            },
            "datePublished": "2023-01-01",
            "dateModified": "2025-06-15",
            "publisher": {
              "@type": "Organization",
              "name": "WebFiny",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
              }
            }
          }
        ]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <Cookie className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-4">
              Cookie Policy
            </h1>
            <p className="text-lg text-muted-foreground">Last Updated: {lastUpdated}</p>
            <p className="text-md text-muted-foreground max-w-2xl mx-auto mt-2">
              This policy explains how WebFiny uses cookies and similar technologies to recognize you when you visit our website and use our services.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto bg-card p-6 sm:p-8 md:p-10 rounded-xl shadow-2xl">
            <nav className="mb-10">
              <h2 className="text-xl font-semibold text-foreground mb-3">Table of Contents</h2>
              <ul className="space-y-1 list-decimal list-inside text-primary">
                {sections.map(section => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="hover:underline hover:text-accent transition-colors">
                      {section.title.substring(section.title.indexOf(" ") + 1)}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            
            {sections.map((section, index) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="mb-8 scroll-mt-24"
              >
                <h2 className="flex items-center text-2xl font-semibold text-foreground mb-4 pb-2 border-b border-border">
                  {section.icon} {section.title}
                </h2>
                <div className="prose prose-lg dark:prose-invert max-w-none text-muted-foreground prose-headings:text-foreground prose-a:text-primary hover:prose-a:underline prose-strong:text-foreground"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </motion.section>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: sections.length * 0.05 }}
              className="mt-12 text-center"
            >
              <Button size="lg" onClick={handleManagePreferences} className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-primary/30">
                <Settings className="w-5 h-5 mr-2" /> Manage Cookie Preferences
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePolicyPage;